import {
  RfaComponents,
  RfaFormControl,
  RfaTransformers,
  RfaValidators,
  type RfaAddressControls,
} from '@innogy/feh/rfa';
import { FormGroup } from '@angular/forms';

import type { LeadAddressFormRendering } from './lead-address-form.interface';
import { fieldToValue } from '../utils/field-to-value';
import { LeadAddressFormDefaults } from './lead-address-form-defaults';

export function getLeadAddressFormControls(
  rendering: LeadAddressFormRendering,
  analyticsComponentName: string = rendering.componentName,
  hasAddressCheck = true
): RfaAddressControls {
  return {
    postcode: new RfaFormControl(
      '',
      {
        label: fieldToValue(
          rendering.fields.PostalCodeLabel,
          LeadAddressFormDefaults.fields.PostalCodeLabel
        ),
        errorMsgs: {
          required: {
            msg: fieldToValue(
              rendering.fields.PostalCodeRequiredError,
              LeadAddressFormDefaults.fields.PostalCodeRequiredError
            ),
            analytics: {
              componentName: analyticsComponentName,
              fieldName: 'postcode',
              description: fieldToValue(
                rendering.fields.PostalCodeRequiredError,
                LeadAddressFormDefaults.fields.PostalCodeRequiredError
              ),
            },
          },
          postcode: {
            msg: fieldToValue(
              rendering.fields.PostalCodeInvalidError,
              LeadAddressFormDefaults.fields.PostalCodeInvalidError
            ),
            analytics: {
              componentName: analyticsComponentName,
              fieldName: 'postcode',
              description: fieldToValue(
                rendering.fields.PostalCodeInvalidError,
                LeadAddressFormDefaults.fields.PostalCodeInvalidError
              ),
            },
          },
        },
        validators: [RfaValidators.required, RfaValidators.postcode],
        transformers: [RfaTransformers.caps, RfaTransformers.trim()],
        helpText: fieldToValue(rendering.fields.PostalCodeHintText),
        analytics: {
          focusChangeTracker: {
            componentName: analyticsComponentName,
            fieldName: 'postcode',
          },
        },
        dataAutomationId: 'address-postalcode',
      },
      RfaComponents.TextInput
    ),
    houseNumber: new RfaFormControl(
      '',
      {
        label: fieldToValue(
          rendering.fields.HouseNumberLabel,
          LeadAddressFormDefaults.fields.HouseNumberLabel
        ),
        errorMsgs: {
          required: {
            msg: fieldToValue(
              rendering.fields.HouseNumberRequiredError,
              LeadAddressFormDefaults.fields.HouseNumberRequiredError
            ),
            analytics: {
              componentName: analyticsComponentName,
              fieldName: 'huisnummer',
              description: fieldToValue(
                rendering.fields.HouseNumberRequiredError,
                LeadAddressFormDefaults.fields.HouseNumberRequiredError
              ),
            },
          },
        },
        transformers: [RfaTransformers.number()],
        validators: [RfaValidators.required],
        helpText: fieldToValue(rendering.fields.HouseNumberHintText),
        analytics: {
          focusChangeTracker: {
            componentName: analyticsComponentName,
            fieldName: 'huisnummer',
          },
        },
        dataAutomationId: 'address-housenumber',
      },
      RfaComponents.TextInput
    ),
    houseNumberAddition: new RfaFormControl(
      '',
      {
        label: fieldToValue(
          rendering.fields.HouseNumberAdditionLabel,
          LeadAddressFormDefaults.fields.HouseNumberAdditionLabel
        ),
        helpText: fieldToValue(rendering.fields.HouseNumberAdditionHintText),
        analytics: {
          focusChangeTracker: {
            componentName: analyticsComponentName,
            fieldName: 'toevoeging',
          },
        },
        dataAutomationId: 'address-housenumberaddition',
      },
      RfaComponents.TextInput
    ),
    street: new RfaFormControl(
      '',
      {
        label: fieldToValue(
          rendering.fields.StreetLabel,
          LeadAddressFormDefaults.fields.StreetLabel
        ),
        errorMsgs: {
          required: {
            msg: fieldToValue(
              rendering.fields.StreetRequiredError,
              LeadAddressFormDefaults.fields.StreetRequiredError
            ),
            analytics: {
              componentName: analyticsComponentName,
              fieldName: 'straat',
              description: fieldToValue(
                rendering.fields.StreetRequiredError,
                LeadAddressFormDefaults.fields.StreetRequiredError
              ),
            },
          },
        },
        validators: hasAddressCheck ? [RfaValidators.required] : [],
        helpText: fieldToValue(rendering.fields.StreetHintText),
        analytics: {
          focusChangeTracker: {
            componentName: analyticsComponentName,
            fieldName: 'straat',
          },
        },
        dataAutomationId: 'address-street',
      },
      RfaComponents.TextInput
    ),
    city: new RfaFormControl(
      '',
      {
        label: fieldToValue(
          rendering.fields.CityLabel,
          LeadAddressFormDefaults.fields.CityLabel
        ),
        errorMsgs: {
          required: {
            msg: fieldToValue(
              rendering.fields.CityRequiredError,
              LeadAddressFormDefaults.fields.CityRequiredError
            ),
            analytics: {
              componentName: analyticsComponentName,
              fieldName: 'stad',
              description: fieldToValue(
                rendering.fields.CityRequiredError,
                LeadAddressFormDefaults.fields.CityRequiredError
              ),
            },
          },
        },
        validators: hasAddressCheck ? [RfaValidators.required] : [],
        helpText: fieldToValue(rendering.fields.CityHintText),
        analytics: {
          focusChangeTracker: {
            componentName: analyticsComponentName,
            fieldName: 'stad',
          },
        },
        dataAutomationId: 'address-city',
      },
      RfaComponents.TextInput
    ),
  };
}

export function createLeadAddressForm(
  rendering: LeadAddressFormRendering
): FormGroup<RfaAddressControls> {
  return new FormGroup(getLeadAddressFormControls(rendering));
}
